import { TaskOutcome } from '@api/generated/graphql';

export const SPEED_TO_LEAD_OUTBOUND_TASK_TYPE = 'speed-to-lead-outbound';

/**
 * Module-level state variants for the v1 outbounding flow.
 *
 * - TASK_CLAIMED_PRE_DIAL: task is claimed and the CSR has not yet dialled (v1 only; replaces
 *   the implicit "claimed" state that v0 inferred from `hasMadeCall === false`).
 * - TASK_POST_CALL_PENDING_DISPOSITION: call has ended; CSR must log an outcome before the task
 *   is completed (used when `featureSpeedToLeadOutboundingAiTaskOutcome` is off).
 * - TASK_POST_CALL_PENDING_DISPOSITION_INBOUND_ACTIVE: same as above, but an inbound call is
 *   currently active so the disposition screen is deferred until the inbound call ends.
 */
export enum SpeedToLeadTaskModuleState {
  TASK_CLAIMED_PRE_DIAL = 'TASK_CLAIMED_PRE_DIAL',
  TASK_POST_CALL_PENDING_DISPOSITION = 'TASK_POST_CALL_PENDING_DISPOSITION',
  TASK_POST_CALL_PENDING_DISPOSITION_INBOUND_ACTIVE = 'TASK_POST_CALL_PENDING_DISPOSITION_INBOUND_ACTIVE',
}

export interface SpeedToLeadContact {
  name: string;
  phoneNumbers: string[];
  address: {
    street: string;
    unit: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
}

export interface SpeedToLeadTaskDetails {
  scriptOpener: string;
  summary: string;
  goal: string;
  history: string;
  refId: string;
  refType: string;
  leadSource?: string | null;
}

export interface SpeedToLeadTaskAttributes {
  tenantId: string;
  action: string;
  outboundNumber: string;
  contact: SpeedToLeadContact;
  taskDetails: SpeedToLeadTaskDetails;
  liveAnswerOutcomeOptions: TaskOutcome[];
  noAnswerOutcomeOptions: TaskOutcome[];
  hasMadeCall?: boolean;
  hasCallConnected?: boolean;
  hasCallEnded?: boolean;
  /**
   * PCS conversation sid for the outbound call, captured on
   * `phone.pcs.conversation.fetched`. Forwarded to the BFF as the JobBooking
   * channel id when the CSR closes the task with a Booked outcome. The same
   * value is also used elsewhere (CallBarManager, PipCallBarManager) to
   * correlate the active call with the task.
   */
  pcsConversationSid?: string;
  /**
   * Set to true after the first telephony failure (phone.call.failed-general).
   * Guards the one-redial limit: on the first failure the Dial CTA is re-enabled
   * and this flag is set; on any subsequent failure the task moves directly to
   * post-call state (hasCallEnded = true) with no further retry offered.
   */
  hasUsedRedial?: boolean;
  /** Unix timestamp (ms) when the acceptance expiry countdown started. Present only while pre-dial. */
  expiryStartedAt?: number;
  /**
   * Re-entrancy guard for the AI-flag auto-log retry loop in handleStlCallFailed.
   * Set to true before the first awaited completeTask attempt so that a duplicate
   * phone.call.failed-general fired during the retries does not start a second
   * concurrent auto-log (which would cause duplicate completeTask calls and a
   * double deleteTask).
   */
  isAutoLogging?: boolean;
}
